import * as z from 'zod';
import {date, id, time} from '../global/globalModels';
import {baseTeam} from '../team/teamModel';

export const selectView = z.object({id: id, name: z.string()});
export const reminderFrequency = z.union([
    z.literal('daily'),
    z.literal('weekly'),
    z.literal('monthly'),
]);

export const bookingRuleType = z.union([
    z.literal('resource_restriction'),
    z.literal('priority_booking'),
    z.literal('permanent_booking'),
]);
export type BookingRuleType = z.infer<typeof bookingRuleType>;

export const bookingRuleStatus = z.union([
    z.literal('active'),
    z.literal('inactive'),
    z.literal('archived'),
    z.literal('planned'),
    z.literal('expired'),
]);
export type BookingRuleStatus = z.infer<typeof bookingRuleStatus>;

export const weekDay = z.union([
    z.literal('monday'),
    z.literal('tuesday'),
    z.literal('wednesday'),
    z.literal('thursday'),
    z.literal('friday'),
    z.literal('saturday'),
    z.literal('sunday'),
]);
export type Weekday = z.infer<typeof weekDay>;
export type Time = z.infer<typeof time>;

const bookingRuleBase = z.object({
    id: id,
    status: bookingRuleStatus,
    title: z.string(),
    dateFrom: date,
    dateUntil: date.nullable(),
    comment: z.string().nullable(),
    weekDays: z.array(weekDay),
    locations: z.array(z.string()),
    floors: z.array(z.string()),
    rooms: z.array(z.string()),
    resources: z.array(z.string()),
    resourceTypes: z.array(z.string()),
    allowedToEdit: z.boolean(),
});

const bookingRestrictionRule = bookingRuleBase.merge(
    z.object({
        type: z.literal('resource_restriction'),
    })
);

const bookingPriorityRule = bookingRuleBase.merge(
    z.object({
        type: z.literal('priority_booking'),
        target: z
            .object({
                teams: baseTeam.array(),
                users: selectView.array(),
            })
            .optional(),
        releaseTime: time.nullable(),
        releaseDays: z.number().nullable(),
    })
);

const permanentBookingRule = bookingRuleBase.merge(
    z.object({
        type: z.literal('permanent_booking'),
        hardSetAssignment: z.boolean(),
        requiresCheckIn: z.boolean(),
        reminderForUpcomingBookings: z.boolean(),
        reminderFrequency: reminderFrequency.nullable(),
        timeFrom: time.nullable(),
        timeUntil: time.nullable(),
        target: z
            .object({
                users: selectView.array(),
            })
            .optional(),
    })
);

const bookingRule = z.discriminatedUnion('type', [
    bookingPriorityRule,
    bookingRestrictionRule,
    permanentBookingRule,
]);

export const resourceRules = z.object({
    id: id,
    bookingRules: bookingRule.array(),
    availableCapacity: z.number(),
});

export type ResourceRules = z.infer<typeof resourceRules>;
